import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import BeforeAfterReport from '../components/BeforeAfterReport';
import { useLanguage } from '../context/LanguageContext';

const ComplaintDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [msg, setMsg] = useState('');

  // Staff Resolution Update State
  const [resStatus, setResStatus] = useState('');
  const [resNotes, setResNotes] = useState('');
  const [resImage, setResImage] = useState(null);
  const [submittingResolution, setSubmittingResolution] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await api.get(`/complaints/${id}`);
      setComplaint(res.data);
      setResStatus(res.data.status);
      setResNotes(res.data.resolution_notes || '');
    } catch (err) {
      console.error('Error fetching complaint details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleUpdateResolution = async (e) => {
    e.preventDefault();
    setSubmittingResolution(true);
    setMsg('');

    try {
      const formData = new FormData();
      formData.append('status', resStatus);
      if (resNotes) formData.append('resolution_notes', resNotes);
      if (resImage) formData.append('resolution_image', resImage);

      await api.put(`/complaints/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResImage(null);
      fetchDetail();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to update resolution.');
    } finally {
      setSubmittingResolution(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    setMsg('');

    try {
      await api.post(`/complaints/${id}/comments`, {
        comment_text: commentText,
        is_internal: isInternal
      });

      setCommentText('');
      setIsInternal(false);
      fetchDetail();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to post comment.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEscalate = async () => {
    if (!window.confirm('Are you sure you want to request an escalation?')) return;
    try {
      await api.post(`/complaints/${id}/escalate`, { reason: 'Escalated by user from detail view' });
      fetchDetail();
    } catch (err) {
      alert(err.response?.data?.message || 'Escalation failed.');
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted small mt-2">{t('loadingTickets')}</p>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning">Complaint ticket not found or access restricted.</div>
        <Link to="/" className="btn btn-primary rounded-pill px-4">{t('backToDashboard')}</Link>
      </div>
    );
  }

  const isStaff = ['agent', 'manager', 'admin', 'dept_head', 'employee'].includes(user?.role);
  const slaDate = new Date(complaint.sla_due_at);
  const isOverdue = slaDate < new Date() && !['Resolved', 'Closed'].includes(complaint.status);

  return (
    <div className="container py-4">
      {/* Back button & Title */}
      <div className="mb-3">
        <Link to={isStaff ? '/staff-dashboard' : '/dashboard'} className="text-decoration-none text-muted small fw-semibold">
          <i className="bi bi-arrow-left me-1"></i> {t('backToDashboard')}
        </Link>
      </div>

      {complaint.is_escalated && (
        <div className="alert alert-danger d-flex align-items-center mb-4 rounded-3 shadow-sm" role="alert">
          <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
          <div>
            <strong className="d-block">{t('escalationNotice')}</strong>
            {t('escalationDesc')}
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Left Column: Complaint Details, Before/After Visual Report & Comments */}
        <div className="col-lg-8">
          <div className="card glass-card border-0 p-4 mb-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <span className="badge bg-dark font-monospace me-2 fs-6">{complaint.ticket_number}</span>
                <span className="badge bg-light text-dark border me-2">{complaint.category}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <StatusBadge priority={complaint.priority} />
                <StatusBadge status={complaint.status} />
              </div>
            </div>

            <h3 className="brand-font fw-bold mb-3">{complaint.title}</h3>
            <p className="text-secondary leading-relaxed mb-4" style={{ whiteSpace: 'pre-wrap' }}>
              {complaint.description}
            </p>

            <div className="p-3 bg-light rounded-3 d-flex flex-wrap justify-content-between gap-3 text-muted small">
              <div><i className="bi bi-person me-1"></i> <strong>{t('submittedBy')}:</strong> {complaint.customer_name} ({complaint.customer_email || complaint.guest_phone})</div>
              <div><i className="bi bi-clock me-1"></i> <strong>{t('dateGenerated')}:</strong> {new Date(complaint.created_at).toLocaleString()}</div>
            </div>
          </div>

          {/* Dedicated Before & After Visual Report */}
          <BeforeAfterReport complaint={complaint} onUpdateRequested={fetchDetail} isStaff={isStaff} />

          {/* Staff Quick Solution & Status Action Panel */}
          {isStaff && (
            <div className="card glass-card border-0 p-4 mb-4 bg-success bg-opacity-10 border border-success border-opacity-25 shadow-sm">
              <h5 className="brand-font fw-bold text-success mb-3">
                <i className="bi bi-check-square-fill me-2"></i> {t('staffSolutionDesk')}
              </h5>

              <form onSubmit={handleUpdateResolution}>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">{t('updateStatus')}</label>
                    <select
                      className="form-select border-success"
                      value={resStatus}
                      onChange={(e) => setResStatus(e.target.value)}
                    >
                      <option value="Submitted">{t('status_Submitted')}</option>
                      <option value="Assigned">{t('status_Assigned')}</option>
                      <option value="In Progress">{t('status_InProgress')}</option>
                      <option value="Pending Customer">{t('status_PendingCustomer')}</option>
                      <option value="Resolved">{t('status_Resolved')}</option>
                      <option value="Closed">{t('status_Closed')}</option>
                      <option value="Escalated">{t('status_Escalated')}</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold small text-success">
                      <i className="bi bi-camera-fill me-1"></i> {t('attachSolutionPhoto')}
                    </label>
                    <input
                      type="file"
                      className="form-control border-success"
                      accept="image/*"
                      onChange={(e) => setResImage(e.target.files[0] || null)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small">{t('resolutionRemarksSummary')}</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder={t('resolutionPlaceholder')}
                    value={resNotes}
                    onChange={(e) => setResNotes(e.target.value)}
                  ></textarea>
                </div>

                <div className="text-end">
                  <button type="submit" className="btn btn-success px-4 font-monospace fw-bold" disabled={submittingResolution}>
                    {submittingResolution ? t('savingResolution') : t('saveAttachSolution')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Comments & Activity Section */}
          <div className="card glass-card border-0 p-4">
            <h5 className="brand-font fw-bold mb-4">
              <i className="bi bi-chat-left-text text-primary me-2"></i> {t('commentsUpdates')} ({complaint.comments?.length || 0})
            </h5>

            {msg && <div className="alert alert-danger small py-2">{msg}</div>}

            <div className="mb-4">
              {complaint.comments && complaint.comments.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {complaint.comments.map((cm) => (
                    <div
                      key={cm.id}
                      className={`p-3 rounded-3 border ${
                        cm.is_internal
                          ? 'bg-warning bg-opacity-10 border-warning border-opacity-25'
                          : 'bg-white'
                      }`}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold small">{cm.author_name}</span>
                          <span className="badge bg-secondary text-capitalize extra-small">{cm.author_role}</span>
                          {cm.is_internal && (
                            <span className="badge bg-warning text-dark extra-small">
                              <i className="bi bi-lock-fill me-1"></i> {t('staffInternalNote')}
                            </span>
                          )}
                        </div>
                        <span className="text-muted extra-small">
                          {new Date(cm.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      </div>
                      <p className="mb-0 text-dark small" style={{ whiteSpace: 'pre-wrap' }}>
                        {cm.comment_text}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small italic">{t('noCommentsYet')}</p>
              )}
            </div>

            {/* Add Comment Form */}
            <form onSubmit={handleAddComment} className="pt-3 border-top">
              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('addResponseRemark')}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t('typeReplyHere')}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  required
                ></textarea>
              </div>

              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                {isStaff && (
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="internalCheck"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                    />
                    <label className="form-check-label small fw-semibold text-warning" htmlFor="internalCheck">
                      <i className="bi bi-eye-slash me-1"></i> {t('makeInternalNote')}
                    </label>
                  </div>
                )}
                <button type="submit" className="btn btn-primary-gradient px-4" disabled={submittingComment}>
                  {submittingComment ? t('posting') : t('postComment')}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Metadata Sidebar & Area Master Details */}
        <div className="col-lg-4">
          {/* Area & Representatives Details Card */}
          <div className="card glass-card border-0 p-4 mb-4 bg-primary bg-opacity-10 border-primary border-opacity-25">
            <h6 className="brand-font fw-bold text-uppercase text-primary mb-3 small">
              <i className="bi bi-geo-alt-fill me-1"></i> {t('areaWardOfficials')}
            </h6>

            {complaint.area_name ? (
              <div className="d-flex flex-column gap-2">
                <div>
                  <span className="text-muted small d-block">{t('registeredWard')}</span>
                  <span className="fw-bold text-dark fs-6">{complaint.area_name}</span>
                  {complaint.area_code && (
                    <span className="badge bg-primary ms-2">{complaint.area_code}</span>
                  )}
                </div>

                {complaint.representatives && complaint.representatives.length > 0 && (
                  <div className="pt-2 border-top border-primary border-opacity-10">
                    <span className="text-muted small d-block mb-1">{t('electedWardOfficials')}</span>
                    <div className="d-flex flex-column gap-1">
                      {complaint.representatives.map(r => (
                        <div key={r.id} className="bg-white p-2 rounded border small">
                          <span className="fw-bold text-primary">{r.name}</span>
                          <span className="badge bg-secondary ms-1 extra-small">{r.designation}</span>
                          {r.contact_info && <small className="d-block text-muted"><i className="bi bi-telephone me-1"></i>{r.contact_info}</small>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted small italic mb-0">{t('notSpecified')}</p>
            )}
          </div>

          {/* Metadata Card */}
          <div className="card glass-card border-0 p-4 mb-4">
            <h6 className="brand-font fw-bold text-uppercase text-muted mb-3 small">{t('ticketAssignment')}</h6>

            <div className="d-flex flex-column gap-3">
              <div>
                <span className="text-muted small d-block">{t('assignedDept')}</span>
                <span className="fw-semibold text-dark">{complaint.department_name || t('unassigned')}</span>
              </div>

              <div>
                <span className="text-muted small d-block">{t('assignedAgent')}</span>
                <span className="fw-semibold text-dark">{complaint.agent_name || t('unassigned')}</span>
              </div>

              <div>
                <span className="text-muted small d-block">{t('slaTargetResolution')}</span>
                <span className={`fw-semibold ${isOverdue ? 'text-danger fw-bold' : 'text-dark'}`}>
                  {slaDate.toLocaleString()}
                  {isOverdue && <span className="badge bg-danger ms-2">{t('slaBreached')}</span>}
                </span>
              </div>
            </div>

            {!complaint.is_escalated && (
              <div className="mt-4 pt-3 border-top">
                <button onClick={handleEscalate} className="btn btn-outline-danger w-100 rounded-pill btn-sm">
                  <i className="bi bi-exclamation-triangle-fill me-1"></i> {t('requestEscalation')}
                </button>
              </div>
            )}
          </div>

          {/* Audit Timeline Card */}
          <div className="card glass-card border-0 p-4">
            <h6 className="brand-font fw-bold text-uppercase text-muted mb-3 small">
              <i className="bi bi-clock-history me-1"></i> {t('auditHistoryTrail')}
            </h6>

            {complaint.logs && complaint.logs.length > 0 ? (
              <div className="timeline-container">
                {complaint.logs.map((log) => (
                  <div key={log.id} className="timeline-item">
                    <div className="timeline-icon"></div>
                    <div className="fw-bold small text-dark">{log.action}</div>
                    <div className="text-muted extra-small">
                      By: {log.performed_by_name} ({log.performed_by_role})
                    </div>
                    <div className="text-muted extra-small">
                      {new Date(log.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted small italic">{t('noAuditLog')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintDetail;
